import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react'

export interface SessionContextType {
    addToSession: (key: string, value: string) => void;
    addJsonToSession: (key: string, value: object) => void;
    getFromSession: (key: string) => string | null;
    getJsonFromSession: (key: string) => object | undefined;
    removeFromSession: (key: string) => void;
    sessionState: Record<string, string>;
}

const SessionContext = createContext<SessionContextType>({} as SessionContextType)
const useSessionContext = () => useContext(SessionContext)

interface Props {
    children: ReactNode
}

const SessionProvider = (props: Props) => {
    // Starts empty (matching the server, which has no sessionStorage) so the first client render
    // is identical to the SSR output - reading sessionStorage here instead would make that first
    // render diverge from the server and trigger a React hydration mismatch. The effect below
    // fills in the real values right after mount, once hydration has already completed.
    const [sessionState, setSessionState] = useState<Record<string, string>>({});

    useEffect(() => {
        const initialState: Record<string, string> = {};
        for (let i = 0; i < window.sessionStorage.length; i++) {
            const key = window.sessionStorage.key(i);
            if (key) {
                initialState[key] = window.sessionStorage.getItem(key) || '';
            }
        }
        setSessionState(initialState);
    }, []);

    const addToSession = useCallback((key: string, value: string) => {
        if (key && typeof window !== "undefined") {
            const currentItem = window.sessionStorage.getItem(key);
            if (currentItem === value) {
                return;
            }
            window.sessionStorage.setItem(key, value);
            const updatedSessionState: Record<string, string> = {};
            for (let i = 0; i < window.sessionStorage.length; i++) {
                const storageKey = window.sessionStorage.key(i);
                if (storageKey) {
                    updatedSessionState[storageKey] = window.sessionStorage.getItem(storageKey) || '';
                }
            }
            setSessionState(updatedSessionState);
        }
    }, [])

    const addJsonToSession = useCallback((key: string, value: object) => {
        if (value) {
            const json = JSON.stringify(value);
            addToSession(key, json);
        }
    }, [addToSession])

    const getFromSession = useCallback((key: string) => {
        if (key && typeof window !== "undefined") {
            return window.sessionStorage.getItem(key);
        }
        return "";
    }, []);

    const getJsonFromSession = useCallback((key: string) => {
        const str = getFromSession(key)
        if (str) {
            return JSON.parse(str);
        }
        return undefined;
    }, [getFromSession]);

    const removeFromSession = useCallback((key: string) => {
        if (key && typeof window !== "undefined") {
            window.sessionStorage.removeItem(key);
            const updatedSessionState: Record<string, string> = {};
            for (let i = 0; i < window.sessionStorage.length; i++) {
                const storageKey = window.sessionStorage.key(i);
                if (storageKey) {
                    updatedSessionState[storageKey] = window.sessionStorage.getItem(storageKey) || '';
                }
            }
            setSessionState(updatedSessionState);
        }
    }, [])

    const contextValue = useMemo(() => ({
        addToSession,
        addJsonToSession,
        getFromSession,
        getJsonFromSession,
        removeFromSession,
        sessionState
    }), [addToSession, addJsonToSession, getFromSession, getJsonFromSession, removeFromSession, sessionState]);

    return (
        <SessionContext.Provider
            value={contextValue}>
            {props.children}
        </SessionContext.Provider>
    )
}


export {
    useSessionContext,
    SessionProvider
}